import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Container, Typography, Stack } from '@mui/material';

import PaySlip from '../components/PaySlip';
import InformationsFrais from '../components/InformationsFrais';
import Header from '../components/Header';

import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';

import { useSimulator } from '../contexts/SimulatorContext';
import { callApi } from '../utils/request';

import './Simulateur.scss';

import Alert from '../components/Alert';

export default function Simulateur() {
    const { id } = useParams();
    const [refresh, setRefresh] = useState(false);
    const { frais, result, setResult, prospectSimulation, initial, setInitial, inputs, checkSimulation } = useSimulator();
    const [variablesFixed, setVariablesFixed] = useState([]);
    
    const [alert, setAlert] = useState(null);
    const [simuAlert, setSimuAlert] = useState(null);

    const [loading, setLoading] = useState(false);

    // Get variables fixes on component mount
    useEffect(() => {
        getVariablesFixes();
    }, []);

    // Re-run the simulation when variablesFixed or refresh changes
    useEffect(() => {
        if (variablesFixed) {
            prospectSimulation(
                id,
                initial,
                variablesFixed,
                alert => {
                    setAlert(alert);
                    setSimuAlert(alert);
                }
            );
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variablesFixed, refresh]);

    const getVariablesFixes = async () => {
        await callApi('/operateur/variablesFixed', 'GET').then((res) => {
            setVariablesFixed(res);
        });
    };

    // Génère le PDF de la simulation et le télécharge automatiquement.
    const handlePDF = () => {
        setLoading(true);

        callApi('/operateur/generatePaySlip', 'POST', { id, ...result }).then((res) => {
            setAlert(res.alert);

            // Si la génération du PDF est réussie, on le télécharge automatiquement.
            if (res.alert.variant === "success") {
                fetch(process.env.REACT_APP_API_URL + `/public/payslip/${id}.pdf`, {
                    method: 'GET',
                    headers: {
                        'Authorization': JSON.parse(localStorage.getItem('user'))?.token
                    }
                })
                .then(res => res.blob())
                .then(file => {
                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(file);
                    link.download = `Simulation - ${id.substring(0, 8)}.pdf`;
                    link.click();
                    link.parentNode?.removeChild(link);
                })
                .finally(() => setLoading(false));
            }
        });
    };

    // Restaure les valeurs initiales de la simulation.
    const valueInitial = () => {
        setInitial(true);
        setAlert({
            variant: 'success',
            message: 'Les valeurs initiales ont été restaurées.'
        });
        setTimeout(() => setAlert(null), 2500);
        setRefresh(!refresh);
    };

    // Lance la simulation avec les valeurs actuelles des inputs et des variables fixes.
    const handleSimulation = () => {
        const errors = checkSimulation();
        if (!errors) {
            callApi('/operateur/updateSimulation', 'POST', { id, inputs, vars: frais, variablesFixed: variablesFixed })
            .then((res) => {
                setResult(res);
                // setRefresh(!refresh);
            }).catch((err) => {
                if (400 === err.errorStatus) {
                    setAlert({ variant: 'warning', message: "Les données envoyées sont invalides. Veuillez vérifier les informations saisies." });
                } else {
                    setAlert({ variant: 'error', message: "Une erreur s'est produite lors de la simulation." });
                }
            });
        }
    };

    // Clôture la simulation en l'archivant et en la retirant de la liste des simulations actives.
    const handleCloture = (id) => {
        callApi('/operateur/clotureSimulation', 'POST', { id: id })
        .then((res) => {
            setAlert(res.alert);
        }).catch((err) => {
            setAlert({ variant: 'error', message: "Une erreur s'est produite lors de l'archivage de la simulation." });
        });
    };

    return (
    <>
        {alert && <Alert alert={alert}/>}
        <Container>
            <Header name="Simulateur de Fiche de Paie" />
            {!result ?
                // Si result est null, cela signifie que les données de la simulation
                // sont en cours de chargement ou qu'une erreur s'est produite. On affiche
                // alors un message d'attente ou d'erreur en fonction de simuAlert.
                (simuAlert && simuAlert.variant === 'error' ?
                    <Typography sx={{ textAlign: 'center', color: 'error.main' }} children={simuAlert.message} />
                    :
                    <Typography sx={{ textAlign: 'center' }} children="Chargement en cours...." />
                )
                :
                // Si result est non null, on affiche le contenu de la simulation : les informations de frais et l'aperçu de la fiche de paie.
                <div className='content'>
                    <div className="left">
                        {/* InformationsFrais affiche les informations de frais de la simulation, avec la possibilité de les modifier si besoin.
                            Les variables fixes sont passées en props pour permettre leur affichage et modification dans ce composant. */}
                        <InformationsFrais variablesFixed={variablesFixed} />

                        {/* Boutons d'action */}
                        <Stack direction="row" spacing={1} sx={{ mt: 2, width: '100%' }}>
                            <Button variant="outlined" color="error" startIcon={<HistoryOutlinedIcon />} onClick={valueInitial} children="Réinitialiser" fullWidth />
                            <Button variant="outlined" color="info" startIcon={<CalculateOutlinedIcon />} onClick={handleSimulation} children="Simuler" fullWidth />
                        </Stack>

                        <Stack spacing={1} sx={{ mt: 1, width: '100%' }}>
                            <Button variant="outlined" color="secondary" startIcon={<FileDownloadOutlinedIcon />} loading={loading} onClick={handlePDF} children="Télécharger le PDF" />
                            <Button variant="outlined" color="success" startIcon={<Inventory2OutlinedIcon />} onClick={() => handleCloture(id)} children="Archiver" />
                        </Stack>
                    </div>

                    {/* Aperçu de la fiche de paie */}
                    <div className='simulator'>
                        <PaySlip payslipVars={result} />
                    </div>
                </div>
            }
        </Container>
    </>
    );
}
